import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { hasSessionHint } from "@/lib/api";

const AuthContext = createContext(null);

// Module-level in-flight promise for /auth/refresh — dedupes parallel calls
// from React.StrictMode double-invocation of useEffect (or any race).
let refreshInFlight = null;
// Same for /auth/me on initial boot.
let meInFlight = null;

async function bootProbe() {
  // Skip entirely if we don't even have a session hint yet.
  if (!hasSessionHint()) return null;
  try {
    const r = await api.get("/auth/me");
    return r.data;
  } catch (err) {
    if (err?.response?.status === 401 && hasSessionHint()) {
      try {
        refreshInFlight = refreshInFlight || api.post("/auth/refresh", {});
        await refreshInFlight;
        refreshInFlight = null;
        const r2 = await api.get("/auth/me");
        return r2.data;
      } catch {
        refreshInFlight = null;
        return null;
      }
    }
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    // Share ONE bootProbe across concurrent invocations (StrictMode double-
    // mount in dev, HMR remounts, etc.). Once resolved, subsequent calls
    // create a fresh probe.
    meInFlight = meInFlight || bootProbe();
    const me = await meInFlight;
    meInFlight = null;
    setUser(me);
  }, []);

  useEffect(() => {
    loadMe().finally(() => setLoading(false));
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };
  const logout = async () => {
    try {
      await api.post("/auth/logout", {});
    } catch (err) {
      if (typeof console !== "undefined") console.warn("logout call failed:", err?.message || err);
    }
    setUser(null);
  };

  const clearSession = useCallback(() => setUser(null), []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout,
                                    refreshUser: loadMe, clearSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }

export const dashboardPath = (role) => ({
  super_admin: "/super/dashboard",
  school_admin: "/school/dashboard",
  parent: "/parent/dashboard",
  vendor: "/vendor/dashboard",
}[role] || "/");
